import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import {
  LayoutDashboard,
  Users,
  Briefcase,
  Calendar,
  DollarSign,
  TrendingUp,
  Activity,
  LogOut,
  Menu,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLORS = ['#0EA5E9', '#10B981', '#F59E0B', '#8B5CF6'];

export default function AdminDashboard({ user, onLogout }) {
  const [stats, setStats] = useState(null);
  const [professionals, setProfessionals] = useState([]);
  const [patients, setPatients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [services, setServices] = useState([]);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [statsRes, profsRes, patientsRes, sessionsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, config),
        axios.get(`${API}/admin/professionals`, config),
        axios.get(`${API}/admin/patients`, config),
        axios.get(`${API}/admin/sessions`, config),
        axios.get(`${API}/admin/services`, config)
      ]);

      setStats(statsRes.data);
      setProfessionals(profsRes.data);
      setPatients(patientsRes.data);
      setSessions(sessionsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      toast.error('Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const revenueData = stats
    ? Object.entries(stats.revenue_by_service).map(([name, value]) => ({
        name,
        revenue: value
      }))
    : [];

  const sessionMixData = stats
    ? Object.entries(stats.session_mix).map(([name, value]) => ({
        name,
        value
      }))
    : [];

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'services', label: 'Services', icon: Activity },
    { id: 'professionals', label: 'Professionals', icon: Briefcase },
    { id: 'patients', label: 'Patients', icon: Users },
    { id: 'sessions', label: 'Sessions', icon: Calendar }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="text-slate-600">Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen w-64 bg-white border-r border-slate-200 z-50 transition-transform lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-[#D32F2F]">Physiomobile</h1>
            <p className="text-sm text-slate-500">Admin Portal</p>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-slate-500"
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveTab(item.id);
                  setSidebarOpen(false);
                }}
                data-testid={`nav-${item.id}-btn`}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                  activeTab === item.id
                    ? 'bg-red-50 text-[#D32F2F]'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <div className="mb-3 px-4 py-3 bg-slate-50 rounded-lg">
            <p className="text-sm font-medium text-slate-900">{user.full_name}</p>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
          <button
            onClick={onLogout}
            data-testid="logout-btn"
            className="w-full flex items-center space-x-3 px-4 py-3 text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-600"
              data-testid="open-sidebar-btn"
            >
              <Menu className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-bold text-slate-900">
              {menuItems.find((item) => item.id === activeTab)?.label}
            </h2>
            <div className="hidden lg:flex items-center space-x-4">
              <span className="text-sm text-slate-600">Welcome back, {user.full_name}</span>
            </div>
          </div>
        </header>

        {/* Dashboard Content */}
        <div className="p-6">
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="stat-card" data-testid="total-patients-card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-sky-50 flex items-center justify-center">
                      <Users className="w-6 h-6 text-sky-500" />
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Total Patients</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.total_patients}</p>
                </div>

                <div className="stat-card" data-testid="total-professionals-card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center">
                      <Briefcase className="w-6 h-6 text-emerald-500" />
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Active Professionals</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.total_professionals}</p>
                </div>

                <div className="stat-card" data-testid="sessions-today-card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                      <Calendar className="w-6 h-6 text-amber-500" />
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Sessions Today</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.sessions_today}</p>
                </div>

                <div className="stat-card" data-testid="sessions-month-card">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-purple-500" />
                    </div>
                  </div>
                  <p className="text-sm text-slate-600 mb-1">Sessions This Month</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.sessions_this_month}</p>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Revenue Chart */}
                <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-slate-900 mb-6">Revenue by Service</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={revenueData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis dataKey="name" stroke="#64748B" />
                      <YAxis stroke="#64748B" />
                      <Tooltip />
                      <Bar dataKey="revenue" fill="#0EA5E9" radius={[8, 8, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Session Mix Chart */}
                <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-slate-900 mb-6">Session Mix</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={sessionMixData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={(entry) => `${entry.name}: ${entry.value}`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {sessionMixData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Additional Stats */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Average Rating by Service</h3>
                  <div className="space-y-3">
                    {Object.entries(stats.avg_rating_by_service).map(([service, rating]) => (
                      <div key={service} className="flex items-center justify-between">
                        <span className="text-slate-700 font-medium">{service}</span>
                        <div className="flex items-center space-x-2">
                          <div className="text-amber-500 font-semibold">{rating.toFixed(1)}</div>
                          <div className="text-xs text-slate-500">★</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-white rounded-xl border border-slate-100 p-6 shadow-sm">
                  <h3 className="text-lg font-bold text-slate-900 mb-4">Key Metrics</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm text-slate-600">Cancellation Rate</span>
                        <span className="text-sm font-semibold text-slate-900">
                          {stats.cancellation_rate.toFixed(1)}%
                        </span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#D32F2F] rounded-full"
                          style={{ width: `${stats.cancellation_rate}%` }}
                        />
                      </div>
                    </div>
                    <div className="pt-4 border-t border-slate-100">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-slate-600">Total Revenue</span>
                        <span className="text-lg font-bold text-emerald-600">
                          RM
                          {Object.values(stats.revenue_by_service)
                            .reduce((a, b) => a + b, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'services' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {services.map((service) => (
                <div key={service.id} className="service-card p-6" data-testid={`service-${service.type}-card`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-1">{service.name}</h3>
                      <p className="text-sm text-slate-600">{service.description}</p>
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        service.enabled
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      {service.enabled ? 'Active' : 'Inactive'}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Home Visit</span>
                      <span className="font-semibold text-slate-900">RM{service.pricing.home}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Clinic Visit</span>
                      <span className="font-semibold text-slate-900">RM{service.pricing.clinic}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Virtual</span>
                      <span className="font-semibold text-slate-900">RM{service.pricing.virtual}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'professionals' && (
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Service</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Specialization</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Rating</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Sessions</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Earnings</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {professionals.map((prof) => (
                      <tr key={prof.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{prof.full_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{prof.service_type}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{prof.specialization}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className="text-amber-500 font-semibold">{prof.rating.toFixed(1)} ★</span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{prof.total_sessions}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-emerald-600">
                          RM{prof.total_earnings.toFixed(2)}
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`px-3 py-1 rounded-full text-xs font-semibold ${
                              prof.available
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-slate-100 text-slate-600'
                            }`}
                          >
                            {prof.available ? 'Available' : 'Unavailable'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'patients' && (
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Name</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Email</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Care Plans</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Sessions</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Loyalty Points</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {patients.map((patient) => (
                      <tr key={patient.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{patient.full_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{patient.email}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{patient.active_care_plans}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{patient.total_sessions}</td>
                        <td className="px-6 py-4 text-sm font-semibold text-[#D32F2F]">
                          {patient.loyalty_points}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'sessions' && (
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Date</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Patient</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Professional</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Service</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Location</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {sessions.map((session) => (
                      <tr key={session.id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {new Date(session.date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{session.patient_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{session.professional_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{session.service_type}</td>
                        <td className="px-6 py-4 text-sm text-slate-600 capitalize">{session.location}</td>
                        <td className="px-6 py-4">
                          <span className={`badge-status badge-${session.status}`}>{session.status}</span>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">RM{session.cost}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
